import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import { useBatchLinkageAnalysis } from "../../../hooks/useLinkageAnalysis";
import { AIModalState } from "../../../enums/CompassAI";
import { HeroKind } from "../../../enums/Modal";
import { MESSAGES } from "../../../messages";
import { AIBatchLinkageResponse } from "../../../types/compassAITypes";

import {
  CompassModal,
  ModalContent,
  ModalFooter,
  ModalSection,
  useCompassModal,
  defineModalConfig,
} from "../Modal";
import { CompassParagraph } from "../styledComponents";
import { BatchLinkageResultsSummary } from "./BatchLinkageResultsSummary";
import {
  CompassGreenButton,
  CompassWhiteButton,
} from "../../buttons/styledComponents";
import { AvatarStack } from "../../visuals/Avatar";
import { RowContainer } from "../../containers/styledComponents";
import { JobProgressBar } from "./JobProgressBar";
import {
  LinkageType,
  LINKAGE_TYPE_DISPLAY,
  InteractionClass,
  INTERACTION_CLASS_DISPLAY,
  SequenceFlow,
  SEQUENCE_FLOW_DISPLAY,
} from "../../../models/engagementVotingMappingModels";

import { darkGreen, errorRed } from "../../../styles";

import aiIcon from "../../../assets/svg/compass_ai_modal_icon.svg";
import engagementLinkageBg from "../../../assets/images/engagement_vote_linkage.png";
import aiJobRunningAnimation from "../../../assets/svg/ai_job_running_animation.svg";

import thematicAlignmentImg from "../../../assets/images/engagement_linkage/thematic_alignment.png";
import complementaryActionImg from "../../../assets/images/engagement_linkage/complementary_action.png";
import escalationPathImg from "../../../assets/images/engagement_linkage/escalation_path.png";
import isolatedActivityImg from "../../../assets/images/engagement_linkage/isolated_activity.png";

import consistentReinforcementImg from "../../../assets/images/engagement_linkage/consistent_reinforcement.png";
import internalInconsistencyImg from "../../../assets/images/engagement_linkage/internal_inconsistency.png";
import synergisticAlignmentImg from "../../../assets/images/engagement_linkage/synergistic_alignment.png";
import activeUnderminingImg from "../../../assets/images/engagement_linkage/active_undermining.png";

import engagementPrecedesVoteImg from "../../../assets/images/engagement_linkage/engagement_precedes_vote.png";
import votePrecedesEngagementImg from "../../../assets/images/engagement_linkage/vote_precedes_engagement.png";
import bidirectionalImg from "../../../assets/images/engagement_linkage/bidirectional.png";

const LINKAGE_TYPE_AVATARS = [
  {
    username: LINKAGE_TYPE_DISPLAY[LinkageType.THEMATIC_ALIGNMENT],
    image: thematicAlignmentImg,
    tooltip: true,
    size: "large" as const,
  },
  {
    username: LINKAGE_TYPE_DISPLAY[LinkageType.COMPLEMENTARY_ACTION],
    image: complementaryActionImg,
    tooltip: true,
    size: "large" as const,
  },
  {
    username: LINKAGE_TYPE_DISPLAY[LinkageType.ESCALATION_PATH],
    image: escalationPathImg,
    tooltip: true,
    size: "large" as const,
  },
  {
    username: LINKAGE_TYPE_DISPLAY[LinkageType.ISOLATED_ACTIVITY],
    image: isolatedActivityImg,
    tooltip: true,
    size: "large" as const,
  },
];

const INTERACTION_CLASS_AVATARS = [
  {
    username:
      INTERACTION_CLASS_DISPLAY[InteractionClass.CONSISTENT_REINFORCEMENT],
    image: consistentReinforcementImg,
    tooltip: true,
    size: "large" as const,
  },
  {
    username:
      INTERACTION_CLASS_DISPLAY[InteractionClass.INTERNAL_INCONSISTENCY],
    image: internalInconsistencyImg,
    tooltip: true,
    size: "large" as const,
  },
  {
    username: INTERACTION_CLASS_DISPLAY[InteractionClass.SYNERGISTIC_ALIGNMENT],
    image: synergisticAlignmentImg,
    tooltip: true,
    size: "large" as const,
  },
  {
    username: INTERACTION_CLASS_DISPLAY[InteractionClass.ACTIVE_UNDERMINING],
    image: activeUnderminingImg,
    tooltip: true,
    size: "large" as const,
  },
];

const SEQUENCE_FLOW_AVATARS = [
  {
    username: SEQUENCE_FLOW_DISPLAY[SequenceFlow.ENGAGEMENT_PRECEDES_VOTE],
    image: engagementPrecedesVoteImg,
    tooltip: true,
    size: "large" as const,
  },
  {
    username: SEQUENCE_FLOW_DISPLAY[SequenceFlow.VOTE_PRECEDES_ENGAGEMENT],
    image: votePrecedesEngagementImg,
    tooltip: true,
    size: "large" as const,
  },
  {
    username: SEQUENCE_FLOW_DISPLAY[SequenceFlow.BIDIRECTIONAL],
    image: bidirectionalImg,
    tooltip: true,
    size: "large" as const,
  },
];

const DownloadButtonsContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  width: 100%;
`;

const messages = MESSAGES.COMPASS_AI.BATCH_LINKAGE;

type ModalContext = { dotCounter: number };

const LINKAGE_MODAL_CONFIG = defineModalConfig<AIModalState, ModalContext>({
  title: messages.MODAL_TITLE,
  titleIcon: aiIcon,
  titleIconSize: 40,
  modalSize: "md",
  size: "lg",
  states: {
    [AIModalState.IDLE]: {
      hero: {
        kind: HeroKind.IMAGE,
        source: engagementLinkageBg,
        padding: 0,
        imageBorderRadius: 8,
        backgroundColor: "#fafafa",
        title: messages.TITLE.IDLE,
        description: messages.DESCRIPTION.IDLE,
      },
    },
    [AIModalState.ANALYZING]: {
      isProcessing: true,
      hero: {
        kind: HeroKind.IMAGE,
        source: aiJobRunningAnimation,
        padding: 0,
        height: 280,
        backgroundColor: "#fafafa",
        title: messages.TITLE.ANALYZING,
        description: messages.DESCRIPTION.ANALYZING,
        suffix: (ctx) =>
          [1, 2, 3].map((i) => (
            <CompassParagraph
              key={i}
              color={ctx.dotCounter >= i ? darkGreen : "transparent"}
              mr={-2}
              fontSize={20}
              bold
            >
              .
            </CompassParagraph>
          )),
      },
    },
    [AIModalState.POLLING]: {
      isProcessing: true,
      hero: {
        kind: HeroKind.IMAGE,
        source: aiJobRunningAnimation,
        padding: 0,
        height: 280,
        backgroundColor: "#fafafa",
        title: messages.TITLE.POLLING,
        description: messages.DESCRIPTION.POLLING,
      },
    },
    [AIModalState.SUCCESS]: {},
    [AIModalState.ERROR]: {
      hero: {
        kind: HeroKind.IMAGE,
        source: engagementLinkageBg,
        padding: 0,
        imageBorderRadius: 8,
        backgroundColor: "#fafafa",
        title: messages.TITLE.ERROR,
        titleColor: errorRed,
        accentColor: errorRed,
        description: messages.DESCRIPTION.ERROR,
      },
    },
  },
});

export interface LinkageAnalysisModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess?: (result: AIBatchLinkageResponse) => void;
}

export const LinkageAnalysisModal: React.FC<LinkageAnalysisModalProps> = ({
  show,
  onHide,
  onSuccess,
}) => {
  const [dotCounter, setDotCounter] = useState(0);

  const { state, startAnalysis, downloadFile, batchResult, progress, reset } =
    useBatchLinkageAnalysis();

  useEffect(() => {
    if (state === AIModalState.ANALYZING) {
      const interval = setInterval(() => {
        setDotCounter((prev) => (prev + 1) % 4);
      }, 500);
      return () => clearInterval(interval);
    }
  }, [state]);

  const handleStartAnalysis = async (): Promise<void> => {
    await startAnalysis();
  };

  useEffect(() => {
    if (state === AIModalState.SUCCESS && batchResult && onSuccess) {
      onSuccess(batchResult);
    }
  }, [state, batchResult]);

  const handleClose = useCallback(() => {
    reset();
    setDotCounter(0);
    onHide();
  }, [reset, onHide]);

  const handleDownloadResults = useCallback(async () => {
    if (batchResult?.output_file_path) {
      const url = await downloadFile(batchResult.output_file_path);
      if (url) window.open(url, "_blank");
    }
  }, [batchResult, downloadFile]);

  const { modalProps, isProcessing } = useCompassModal({
    config: LINKAGE_MODAL_CONFIG,
    state,
    context: { dotCounter },
  });

  const renderFooter = (): React.ReactNode => {
    if (state === AIModalState.SUCCESS) {
      return (
        <DownloadButtonsContainer>
          {batchResult?.output_file_path && (
            <CompassGreenButton onClick={handleDownloadResults}>
              {messages.DOWNLOAD_RESULTS}
            </CompassGreenButton>
          )}
          <CompassWhiteButton onClick={handleClose}>Done</CompassWhiteButton>
        </DownloadButtonsContainer>
      );
    }

    if (state === AIModalState.IDLE) {
      return (
        <ModalFooter
          onClose={handleClose}
          closeText="Cancel"
          submitText={messages.START_ANALYSIS}
          onSubmit={handleStartAnalysis}
          showDivider={false}
        />
      );
    }

    if (state === AIModalState.ERROR) {
      return (
        <ModalFooter
          onClose={handleClose}
          closeText="Close"
          submitText="Try Again"
          onSubmit={reset}
          showDivider={false}
        />
      );
    }

    return (
      <ModalFooter
        onClose={handleClose}
        closeText="Cancel"
        hideSubmit
        isLoading={isProcessing}
        showDivider={false}
      />
    );
  };

  return (
    <CompassModal
      show={show}
      onHide={handleClose}
      {...modalProps}
      footer={renderFooter()}
    >
      <ModalContent>
        {state === AIModalState.IDLE && (
          <RowContainer jc="space-between" ai="flex-start">
            <ModalSection title="Linkage Type">
              <AvatarStack
                items={LINKAGE_TYPE_AVATARS}
                maxCount={4}
                enableExpand={false}
              />
            </ModalSection>
            <ModalSection title="Interaction Class">
              <AvatarStack
                items={INTERACTION_CLASS_AVATARS}
                maxCount={4}
                enableExpand={false}
              />
            </ModalSection>
            <ModalSection title="Sequence Flow">
              <AvatarStack
                items={SEQUENCE_FLOW_AVATARS}
                maxCount={3}
                enableExpand={false}
              />
            </ModalSection>
          </RowContainer>
        )}

        {state === AIModalState.POLLING && progress && (
          <JobProgressBar progress={progress} />
        )}

        {state === AIModalState.SUCCESS && batchResult && (
          <BatchLinkageResultsSummary result={batchResult} />
        )}
      </ModalContent>
    </CompassModal>
  );
};
